import { f7 } from 'framework7-react';
import { useState } from 'react';
import { loginByNativeApi, switchCustomerAPITokenApi } from '@/api/authApi';
import { isApp } from '@/config/device';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { message } from '@/consts/login';
import { url } from '@/consts/url';
import { LoginApiParams, LoginByNativeApiParams } from '@/types/api/authApi';
import { clearLoginTel } from '@/utils/loginTel';

export const useLogin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const login = async (params: LoginApiParams, isDuplicated?: boolean) => {
    try {
      f7.preloader.show();
      setIsLoading(true);
      await determineDeviceAndCallLogin(params, isDuplicated);
      if (!params.m_affiliate_id) {
        await store.dispatch('setIsLoggedInToMaikuru', {
          isLoggedInToMaikuru: true,
        });
      }
      store.dispatch('setDiagnosticDate', { diagnosticDate: null });
      if (isDuplicated && store.state.globalTheme == null) {
        f7.views.main.router.navigate(paths.emailDuplicated, {
          props: {
            onSelectCustomer: async (customer: { id: number }) => {
              const selectedAuth = await requestCustomerSwitchAuth(customer.id);
              if (selectedAuth.customerId !== customer.id) {
                throw new Error(
                  '選択したアカウントの認証情報を取得できませんでした',
                );
              }
              await applyEmailAuth(selectedAuth);
              notifyNativeLogin(selectedAuth);
            },
          },
        });
      } else {
        f7.views.main.router.navigate(paths.home);
      }
    } catch (err) {
      if (err instanceof Error) {
        f7.dialog.alert(err?.message, 'エラー');
        setError(err);
      }
    } finally {
      setIsLoading(false);
      f7.preloader.hide();
    }
  };

  const loginByNative = async (
    params: LoginByNativeApiParams,
    isDuplicated?: boolean,
  ) => {
    const auth = await requestEmailAuth(params);
    await applyEmailAuth(auth);
    if (!isDuplicated) {
      notifyNativeLogin(auth);
    }
  };

  const requestEmailAuth = async (params: LoginByNativeApiParams) => {
    const { data: loginData } = await loginByNativeApi(params);
    if (!loginData.success) {
      const obj = loginData.error;
      const messages = Object.entries(obj ?? {}).flatMap(
        ([key, value]) => value,
      );
      throw new Error(messages.join('\n') || message.DEFAULT_LOGIN_ERROR);
    }
    return {
      customerId: loginData.m_customers.id,
      token: decodeURI(loginData.token),
    };
  };

  const requestCustomerSwitchAuth = async (customerId: number) => {
    const { data: switchData } = await switchCustomerAPITokenApi({
      m_customer_id: customerId,
    });
    if (
      !switchData.success ||
      !switchData.token ||
      !switchData.m_customers?.id
    ) {
      throw new Error(
        switchData.error?.message ||
          switchData.message ||
          message.DEFAULT_LOGIN_ERROR,
      );
    }
    return {
      customerId: switchData.m_customers.id,
      token: decodeURI(switchData.token),
    };
  };

  const applyEmailAuth = async (auth: {
    customerId: number;
    token: string;
  }) => {
    await store.dispatch('setAuthInfo', {
      authInfo: {
        m_customer_id: auth.customerId,
        token: auth.token,
        isLogin: true,
      },
    });
    clearLoginTel();
    await store.dispatch('setLoginInfo', {
      loginInfo: { tel: '' },
    });
  };

  const notifyNativeLogin = (auth: { customerId: number; token: string }) => {
    if (isApp) {
      const desiredUrl = `${url.NATIVE_BASE_URL}/set/login_success?token=${auth.token}&id=${auth.customerId}&type=email`;
      window.location.href = desiredUrl;
    }
  };

  const determineDeviceAndCallLogin = async (
    // isApp: boolean,
    params: LoginApiParams,
    isDuplicated?: boolean,
  ) => {
    return await loginByNative(
      { ...params, token_abilities: [] },
      isDuplicated,
    );
  };

  return {
    login,
    isLoading,
    error,
  };
};
